import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import { fetchReports } from "../store/researchReportSlice";
import { fetchIndustries } from "../store/industrySlice";
import { IoCallOutline, IoMailOutline } from "react-icons/io5";
import {
  FaLinkedinIn,
  FaYoutube,
  FaMapMarkerAlt,
  FaRegMap,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import "../styles/footer.scss";
import logo from "../assets/images/RW_Logo_SVG.svg";
import { slugifyIndustry } from "../utils/industryData";

const Footer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { hierarchy: fetchedIndustries, fetched: industriesFetched } =
    useSelector(
      (state) => state.industries || { hierarchy: {}, fetched: false },
    );
  const { fetched } = useSelector(
    (state) => state.researchReports || { fetched: false },
  );

  useEffect(() => {
    if (!fetched) {
      dispatch(fetchReports());
    }
    if (!industriesFetched) {
      dispatch(fetchIndustries());
    }
  }, [dispatch, fetched, industriesFetched]);

  const dynamicIndustries = useMemo(() => {
    const validHierarchy =
      fetchedIndustries &&
      typeof fetchedIndustries === "object" &&
      !Array.isArray(fetchedIndustries)
        ? fetchedIndustries
        : {};
    const dynamicKeys = Object.keys(validHierarchy);
    if (dynamicKeys.length === 0) {
      return [
        "Healthcare",
        "ICT",
        "Automotive",
        "Power",
        "Construction and Manufacturing",
        "Consumer Goods and Retail",
        "BFSI",
      ].sort((a, b) => a.localeCompare(b));
    }
    return dynamicKeys.sort((a, b) => a.localeCompare(b));
  }, [fetchedIndustries]);

  const isReportViewPage = location.pathname.includes("/reports/");

  return (
    <footer className={`${isReportViewPage ? "pb-fixed-actions" : ""}`}>
      <div className="container-fluid">
        <div className="footer-section-unified pt-5 row g-4 mb-0 px-4 px-md-3">
          {/* Col 1: Brand & Social */}
          <div className="col-12 col-lg-4 pe-lg-4 d-flex flex-column align-items-start">
            <Link
              to="/"
              className="mb-3 d-inline-block"
              aria-label="Researchwire Home"
            >
              <img
                src={logo}
                alt="Researchwire Logo"
                className="footer-logo-img"
              />
            </Link>
            <p
              className="text-muted mb-4 small lh-lg pe-5"
              style={{ textAlign: "justify", fontSize: "0.95rem" }}
            >
              Researchwire is a global market research and consulting firm
              delivering syndicated reports, custom research, competitive
              intelligence, and IP research that empower organizations to make
              confident business decisions.
            </p>
            <h6 className="fw-bold mb-3 text-dark small text-uppercase tracking-wider">
              Follow Us
            </h6>
            <div className="d-flex gap-3 social-icons-unified">
              <a
                href="https://www.linkedin.com/company/researchwire/posts/?feedView=all"
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn />
              </a>
              <a
                href="https://x.com/researchwire"
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                aria-label="X (Twitter)"
              >
                <FaXTwitter />
              </a>
              <a
                href="https://www.youtube.com/@researchwire5774"
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                aria-label="YouTube"
              >
                <FaYoutube />
              </a>
            </div>
          </div>

          {/* Col 2: Quick Links */}
          <div className="col-6 col-lg-2 text-start">
            <h6 className="fw-bold mb-4 text-dark text-uppercase tracking-wider small">
              Quick Links
            </h6>
            <ul className="footer-links list-unstyled">
              <li>
                <Link to="/" target="_blank" rel="noopener noreferrer">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about-us" target="_blank" rel="noopener noreferrer">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/services" target="_blank" rel="noopener noreferrer">
                  Services
                </Link>
              </li>
              <li>
                <a
                  href="https://www.researchwire.in/careers/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Careers
                </a>
              </li>
              <li>
                <Link
                  to="/disclaimer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Disclaimer
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/cookies" target="_blank" rel="noopener noreferrer">
                  Cookies
                </Link>
              </li>
              <li>
                <Link
                  to="/how-to-order"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  How to Order
                </Link>
              </li>
              <li>
                <Link to="/sitemap" target="_blank" rel="noopener noreferrer">
                  Sitemap
                </Link>
              </li>
              <li>
                <Link
                  to="/contact-us"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Top Industries */}
          <div className="col-6 col-lg-3 text-start">
            <h6 className="fw-bold mb-4 text-dark text-uppercase tracking-wider small">
              Top Industries
            </h6>
            <ul className="footer-links list-unstyled mb-0">
              {dynamicIndustries.slice(0, 9).map((ind, i) => (
                <li key={i}>
                  <Link to={`/industries/${slugifyIndustry(ind)}`}>{ind}</Link>
                </li>
              ))}
              <li className="mt-2 pt-1">
                <Link
                  to="/industries"
                  style={{
                    color: "#053b84",
                    fontWeight: "600",
                    textDecoration: "none",
                  }}
                >
                  Explore all industries &rarr;
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Contact Info & Addresses */}
          <div className="col-12 col-lg-3 text-start">
            <h6 className="fw-bold mb-4 text-dark text-uppercase tracking-wider small">
              Contact Info
            </h6>
            <ul className="contact-info list-unstyled mb-4">
              <li className="d-flex align-items-start mb-2">
                <IoCallOutline
                  className="me-3 fs-5 mt-1"
                  style={{ color: "#053b84" }}
                />
                <a
                  href="tel:+912245133202"
                  className="text-muted text-decoration-none hover-primary transition-colors"
                >
                  (+91) 22 – 45133202
                </a>
              </li>
              <li className="d-flex align-items-start mb-3">
                <IoMailOutline
                  className="me-3 fs-5 mt-1"
                  style={{ color: "#053b84" }}
                />
                <a
                  href="mailto:query@researchwire.in"
                  className="text-muted text-decoration-none hover-primary transition-colors"
                >
                  query@researchwire.in
                </a>
              </li>

              {/* Added Addresses smoothly under contact info */}
              <li className="d-flex align-items-start mb-2">
                <FaMapMarkerAlt
                  className="me-3 fs-5 mt-1 flex-shrink-0"
                  style={{ color: "#053b84" }}
                />
                <div>
                  <strong className="d-block text-dark small mb-0">
                    Luxembourg, Europe
                  </strong>
                  <span
                    className="text-muted d-block mb-2"
                    style={{ fontSize: "0.8rem", lineHeight: "1.4" }}
                  >
                    Researchwire Europe SARL, 61 Wisestrooss, Hellange 3336
                  </span>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=ResearchWire+Europe+SARL,+61+Wisestrooss,+Hellange+3336"
                    target="_blank"
                    rel="noreferrer"
                    className="map-btn d-inline-flex align-items-center gap-1"
                  >
                    <FaRegMap /> Map
                  </a>
                </div>
              </li>

              <li className="d-flex align-items-start mb-2">
                <FaMapMarkerAlt
                  className="me-3 fs-5 mt-1 flex-shrink-0"
                  style={{ color: "#053b84" }}
                />
                <div>
                  <strong className="d-block text-dark small mb-0">
                    Mumbai, India
                  </strong>
                  <span
                    className="text-muted d-block mb-2"
                    style={{ fontSize: "0.8rem", lineHeight: "1.4" }}
                  >
                    B-402, Rupa Solitaire, MBP, Mahape, Navi Mumbai, MH 400701
                  </span>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=402,+Rupa+Solitaire,+MBP,+Mahape,+Navi+Mumbai,+MH+400701"
                    target="_blank"
                    rel="noreferrer"
                    className="map-btn d-inline-flex align-items-center gap-1"
                  >
                    <FaRegMap /> Map
                  </a>
                </div>
              </li>

              <li className="d-flex align-items-start mb-2">
                <FaMapMarkerAlt
                  className="me-3 fs-5 mt-1 flex-shrink-0"
                  style={{ color: "#053b84" }}
                />
                <div>
                  <strong className="d-block text-dark small mb-0">
                    Noida, India
                  </strong>
                  <span
                    className="text-muted d-block mb-2"
                    style={{ fontSize: "0.8rem", lineHeight: "1.4" }}
                  >
                    901-908, Tower C, Ithum Tower, Block A, Sector 62, UP 201309
                  </span>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=901-908,+Tower+C,+Ithum+Tower,+Block+A,+Sector+62,+UP+201309"
                    target="_blank"
                    rel="noreferrer"
                    className="map-btn d-inline-flex align-items-center gap-1"
                  >
                    <FaRegMap /> Map
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="footer-copyright py-3">
        <div className="container text-center text-md-start">
          <p className="small mb-0">
            &copy; {new Date().getFullYear()} Researchwire. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
